import React, { useState, useRef, useEffect } from 'react';
import { AiOutlineClose, AiOutlineDownload, AiOutlineCamera } from 'react-icons/ai';
import { FaWindows, FaApple } from 'react-icons/fa';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  exportDataToCSV: (userName?: string, message?: string) => void;
  defaultUserName?: string;
  triggerRef?: React.RefObject<HTMLButtonElement>;
  darkMode?: boolean;
}

const ExportModal: React.FC<ExportModalProps> = ({ 
  isOpen, 
  onClose, 
  exportDataToCSV,
  defaultUserName = '',
  triggerRef,
  darkMode = false
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const [isVisible, setIsVisible] = useState(false);

  // Handle visibility with animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Calculate position based on trigger button location
  useEffect(() => {
    if (isOpen && triggerRef?.current && modalRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: triggerRect.bottom + 10,
        right: window.innerWidth - triggerRect.right
      });
    }
  }, [isOpen, triggerRef]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current && 
        !modalRef.current.contains(event.target as Node) && 
        triggerRef?.current !== event.target && 
        !triggerRef?.current?.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isVisible && !isOpen) return null;

  const handleSaveToComputer = () => {
    exportDataToCSV(defaultUserName);
    onClose();
  };

  return (
    <div 
      ref={modalRef}
      className={`fixed z-50 transition-all duration-200 ease-in-out ${
        isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`,
      }}
    >
      <div className="bg-white rounded-xl w-[400px] border border-gray-200 shadow-xl">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Export Options</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <AiOutlineClose className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <button
                onClick={handleSaveToComputer}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium transition-colors duration-200"
              >
                <div className="flex items-center gap-3">
                  <AiOutlineDownload className="h-5 w-5" />
                  <span>Download CSV</span>
                </div>
                <div className="bg-white/20 px-2 py-1 rounded text-xs">
                  Recommended
                </div>
              </button>
              <p className="mt-3 text-xs text-gray-600">
                Export your timetable data as a CSV file to save or share with others
              </p>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white border border-gray-200 text-gray-800 rounded-lg text-sm">
                <div className="flex items-center gap-3">
                  <AiOutlineCamera className="h-5 w-5" />
                  <span>Take a Screenshot of Timetable</span>
                </div>
              </div>
              <div className="mt-3 space-y-3">
                <p className="text-xs text-gray-600">
                  To capture your timetable as an image, use your system's screenshot tool:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-gray-600 bg-white p-2 rounded border border-gray-100">
                    <FaWindows className="h-4 w-4 text-blue-500 shrink-0" />
                    <span>Windows: Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">Windows</kbd> + <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">Shift</kbd> + <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">S</kbd>, then select the timetable area</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600 bg-white p-2 rounded border border-gray-100">
                    <FaApple className="h-4 w-4 text-gray-700 shrink-0" />
                    <span>Mac: Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">⌘</kbd> + <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">Shift</kbd> + <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">4</kbd>, then select the timetable area</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal; 